import type { ReactNode } from 'react'
import React, { createContext, useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDebounce } from 'use-debounce'

import { useAuth } from '@fourthwall/auth'
import { useModalContext } from '@fourthwall/components'
import { useFormik } from '@fourthwall/utils/dist/formik'
import { useCreateImageResources } from '@modules/Dashboard/Memberships/hooks/useCreateImageResources'
import { useCurrentShopQuery } from '@modules/Dashboard/hooks/useCurrentShopQuery'
import { useUsersQuery } from '@queries/useUsersQuery'
import { reverse, routing } from '@utils/routing'

import { getCategoryAndSubcategoryFromSelectedCategory } from '../../Podcast/utils'
import type { ExistingPodcastFormValues, NewPodcastFormValues, Tab } from '../NewPodcastModal.types'
import { existingPodcastFormvalidationSchema, newPodcastFormvalidationSchema } from '../consts'
import { useCreatePodcastFromUrlMutation } from '../queries/useCreatePodcastFromUrlMutation'
import { useCreatePodcastMutation } from '../queries/useCreatePodcastMutation'
import { usePodcastPreviewQuery } from '../queries/usePodcastPreviewQuery'

type Props = { children: ReactNode }

type NewPodcastModalContextType = {
  tab: Tab
  setTab: (tab: Tab) => void
  existingPodcastForm: ReturnType<typeof useFormik<ExistingPodcastFormValues>>
  newPodcastForm: ReturnType<typeof useFormik<NewPodcastFormValues>>
  preview: ReturnType<typeof usePodcastPreviewQuery>['podcastPreviewQuery']
  addArtwork: (files: File[]) => Promise<void>
  removeArtwork: () => void
  isLoading: boolean
}

export const NewPodcastModalContext = createContext<NewPodcastModalContextType | undefined>(
  undefined,
)

export const NewPodcastModalProvider = ({ children }: Props) => {
  const navigate = useNavigate()
  const modal = useModalContext()
  const { currentShopQuery } = useCurrentShopQuery()
  const { user } = useAuth()
  const { usersQuery } = useUsersQuery()
  const [tab, setTab] = useState<Tab>('existing')
  const [artworkId, setArtworkId] = useState<number | undefined>(undefined)

  const { createImageResources, isLoading: createImageResourcesLoading } = useCreateImageResources()

  const { createPodcastFromUrlMutation } = useCreatePodcastFromUrlMutation()
  const { createPodcastMutation } = useCreatePodcastMutation()

  const superAdminEmail = useMemo(() => {
    if (!usersQuery.data) return undefined
    if (!user) return undefined

    const superAdminUsers = usersQuery.data.users.filter((user) => user.group === 'OWNER')

    if (superAdminUsers.length === 0) {
      return user.email
    }

    const loggedInSuperAdmin = superAdminUsers.find(
      (superAdminUser) => superAdminUser.email === user.email,
    )

    const firstSuperAdmin = superAdminUsers[0]

    if (!firstSuperAdmin) return user.email
    if (!firstSuperAdmin.email) return user.email

    return loggedInSuperAdmin?.email ? loggedInSuperAdmin.email : firstSuperAdmin.email
  }, [usersQuery, user])

  const existingPodcastForm = useFormik<ExistingPodcastFormValues>({
    initialValues: {
      url: '',
    },
    onSubmit: (values) => {
      if (!superAdminEmail) return

      createPodcastFromUrlMutation.mutate(
        [
          {
            creatorEmail: superAdminEmail,
            url: values.url,
          },
        ],
        {
          onSuccess: (response) => {
            modal.close()
            navigate(reverse(routing.apps.spotify.podcast.self, { podcastId: response.id }))
          },
        },
      )
    },
    enableReinitialize: true,
    validationSchema: existingPodcastFormvalidationSchema,
    validateOnMount: true,
  })

  const [existingPodcastFormUrlValueDebounced] = useDebounce(existingPodcastForm.values.url, 500)

  const { podcastPreviewQuery } = usePodcastPreviewQuery(
    [{ url: existingPodcastFormUrlValueDebounced }],
    {
      enabled:
        existingPodcastForm.isValid &&
        !!existingPodcastForm.values.url &&
        !existingPodcastForm.isValidating &&
        existingPodcastFormUrlValueDebounced === existingPodcastForm.values.url,
      retry: false,
      cacheTime: 0,
      onError: (error: any) => {
        if (error.status === 404 || error.status === 400) {
          return undefined
        }
      },
    },
  )

  const newPodcastForm = useFormik<NewPodcastFormValues>({
    initialValues: {
      title: '',
      description: '',
      authorName: currentShopQuery.data?.name || '',
      contactEmail: superAdminEmail || '',
      artworkUrl: '',
      category: '',
    },
    onSubmit: (values) => {
      if (!artworkId) return

      const { category, subcategory } = getCategoryAndSubcategoryFromSelectedCategory(
        values.category,
      )

      createPodcastMutation.mutate(
        [
          {
            name: values.title,
            description: values.description,
            creatorName: values.authorName,
            creatorEmail: values.contactEmail,
            imageResourceId: artworkId,
            category,
            subcategory: subcategory || undefined,
          },
        ],
        {
          onSuccess: (response) => {
            modal.close()
            navigate(reverse(routing.apps.spotify.podcast.self, { podcastId: response.id }))
          },
        },
      )
    },
    enableReinitialize: true,
    validationSchema: newPodcastFormvalidationSchema,
    validateOnMount: true,
  })

  const addArtwork = useCallback(
    async (files: File[]) => {
      createImageResources({
        files,
        onSuccess: (images) => {
          setArtworkId(images[0].id)
          newPodcastForm.setFieldValue('artworkUrl', images[0].url)
        },
      })
    },
    [createImageResources, newPodcastForm],
  )

  const removeArtwork = useCallback(() => {
    newPodcastForm.setFieldValue('artworkUrl', '')
  }, [newPodcastForm])

  const value = useMemo(
    () => ({
      tab,
      setTab,
      newPodcastForm,
      existingPodcastForm,
      preview: podcastPreviewQuery,
      addArtwork,
      removeArtwork,
      isLoading:
        usersQuery.isLoading ||
        currentShopQuery.isLoading ||
        createImageResourcesLoading ||
        createPodcastMutation.isLoading ||
        createPodcastFromUrlMutation.isLoading ||
        podcastPreviewQuery.isFetching,
    }),
    [
      tab,
      newPodcastForm,
      existingPodcastForm,
      addArtwork,
      removeArtwork,
      usersQuery,
      currentShopQuery,
      createPodcastMutation,
      createImageResourcesLoading,
      createPodcastFromUrlMutation,
      podcastPreviewQuery,
    ],
  )

  return <NewPodcastModalContext.Provider value={value}>{children}</NewPodcastModalContext.Provider>
}
